/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * DataBindingPanel — shared UI for connecting a section to a database table.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiHelper } from '../../hooks/usePages';
import { useTheme } from '../../theme/ThemeContext';

export default function DataBindingPanel({ appId, binding, onChange }) {
  const { theme } = useTheme();

  const { data: tablesData } = useQuery({
    queryKey: ['tableList', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page_size=200`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return { tables: [] };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const tables = tablesData?.tables || tablesData?.results || [];
  const selectedTable = tables.find(t => t.slug === binding?.table_slug || t.name === binding?.table_slug);

  const { data: columnsData } = useQuery({
    queryKey: ['tableColumns', selectedTable?.id],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/details/?table_id=${selectedTable.id}`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return { fields: [] };
      return resp.json();
    },
    enabled: !!selectedTable?.id,
    staleTime: 60_000,
  });

  const fields = columnsData?.fields || [];

  const updateBinding = (patch) => onChange({ ...(binding || {}), ...patch });

  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };
  const inputStyle = {
    width: '100%',
    padding: '6px 10px',
    fontSize: 13,
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    outline: 'none',
  };

  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 10 }}>
        DATA BINDING
      </div>

      {/* Table selector */}
      <div style={{ marginBottom: 10 }}>
        <div style={labelStyle}>Source Table</div>
        <select
          style={inputStyle}
          value={binding?.table_slug || ''}
          onChange={e => updateBinding({ table_slug: e.target.value, field_mapping: {} })}
        >
          <option value="">— Select table —</option>
          {tables.filter(t => t.visibility === 'public').map(t => (
            <option key={t.id} value={t.slug || t.name}>
              {t.display_name || t.name}
            </option>
          ))}
        </select>
        {tables.filter(t => t.visibility !== 'public').length > 0 && (
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            Only public tables are shown.
          </div>
        )}
      </div>

      {/* Field mapping */}
      {selectedTable && fields.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={labelStyle}>Field Mapping</div>
          {['title', 'description', 'image', 'badge', 'date'].map(role => (
            <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: '#64748b', width: 70, flexShrink: 0 }}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </span>
              <select
                style={{ ...inputStyle, flex: 1 }}
                value={binding?.field_mapping?.[role] || ''}
                onChange={e => updateBinding({
                  field_mapping: { ...(binding?.field_mapping || {}), [role]: e.target.value || undefined },
                })}
              >
                <option value="">— None —</option>
                {fields.map(f => (
                  <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* Limit */}
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>Initial Records</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
            {[3, 6, 9, 12].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => updateBinding({ limit: n })}
                style={{
                  flex: 1, padding: '4px 0', fontSize: 12, fontWeight: 600,
                  border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer',
                  background: (binding?.limit || 10) === n ? theme.primary : '#fff',
                  color: (binding?.limit || 10) === n ? '#fff' : '#374151',
                }}
              >
                {n}
              </button>
            ))}
          </div>
          <input
            type="number"
            style={{ ...inputStyle, fontSize: 12 }}
            min={1}
            max={500}
            value={binding?.limit || 10}
            onChange={e => updateBinding({ limit: parseInt(e.target.value) || 10 })}
            placeholder="Custom…"
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>Sort By</div>
          <select
            style={inputStyle}
            value={binding?.sort?.field || ''}
            onChange={e => updateBinding({ sort: e.target.value ? { field: e.target.value, direction: 'desc' } : null })}
          >
            <option value="">Default</option>
            {fields.map(f => (
              <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
